<script lang="ts">
  import type { Page } from "@sveltejs/kit";
  import type { LayoutServerData } from "./$types";
  
	import { GAP, MIN_BANNER_WIDTH, MIN_PRODUCT_WIDTH } from "$lib/serv/layout";
  import { browser } from "$app/environment";
  import { beforeNavigate, onNavigate } from "$app/navigation";
  import { page, updated } from "$app/stores";
  import { getRouteName } from "$lib/serv/analytics";
  import { apiError$ } from "$lib/serv/core/auth";
  import { getItem, setItem } from "$lib/serv/core/local-store";
  import { setBrowsingLocation } from "$lib/serv/locations";
  import { logNavigation } from "$lib/serv/vendor-integration/firebase";
  import { setupClient as setupRecaptchaClient } from "$lib/serv/vendor-integration/recaptcha";
  import { getRouteSlug } from "$lib/util/seo";
  import { onMount } from "svelte";
  import { setVersion } from "$lib/serv/core/env";

  import Toasts from '$lib/ui/layout/Toasts.svelte';
  import InfoDump from "$lib/ui/profile/InfoDump.svelte";
  import Error from "$lib/ui/shared/Error.svelte";
  import TrackBanner from "$lib/ui/shared/TrackBanner.svelte";

	import '../scss/font.scss';
	import '../scss/main.scss';

	export let data: LayoutServerData;

  if (browser) setVersion(data.version);
	
	// config.onUnhandledError = (error) => {
	// 	// TODO: clean out non-Error errors (failed fetches, etc.) before sending to Sentry
	// };
  
  /**
   * In an attempt to reduce failed module loads
   * See https://kit.svelte.dev/docs/configuration#version
  */
  beforeNavigate(({ willUnload, to }) => {
    // if an update is found force a reload to the `to` url
		if ($updated && !willUnload && to?.url) {
			location.href = to.url.href;
		}
	});

  $: onPage($page);

  function onPage({ route, params, url }: Page) {
    setBrowsingLocation(Object.fromEntries(url.searchParams.entries()));

    // Analytics - log screen name
    const name = getRouteName(route.id, params, url.searchParams);
    logNavigation(name);

    //debugger
    if (url.searchParams.has('dev-mode')) {
      const v = url.searchParams.get('dev-mode')?.toLowerCase();
      setItem('dev-mode', v === '1' || v === 'true' || v === 'on' || v === 'yes');
    }

    // meta route identifier
    let id = getRouteSlug(route);
    if (getItem<boolean>('dev-mode')) console.info(`Route ID: "${id}"`);
  }

  $: err = $apiError$;

  const refresh = () => {
    window.location.reload();
  }

  onMount(() => {
    if (browser) {
      setupRecaptchaClient();
    };
  })
</script>


<!-- Uses LS and will show when LS is cleared (for instance on logout) -->
{#if !data.is_crawler}
  <TrackBanner />
{/if}

<Toasts />

<div class="wrapper padded max-w-1600" style={`
	--min-product-width: ${MIN_PRODUCT_WIDTH}px;
	--min-banner-width: ${MIN_BANNER_WIDTH}px;
	--gap: ${GAP}px;`}>
  {#if err}
    {@const { error_code } = err}
    {#if error_code === 'API_DOWN'}
      <Error title="Uh Oh!" description="The system is down for maintenance and will be back shortly." buttonText="Refresh" on:exit={refresh} />
    {:else if error_code === 'APP_SHUT_OUT'}
      <Error title="Uh Oh!" description="This app version is no longer supported. Please refresh." buttonText="Refresh" on:exit={refresh} />
    {:else}
      <Error title="Uh oh!" description="We encountered a problem and need to refresh the app." buttonText="Refresh" on:exit={refresh} />
    {/if}
  {:else}
    <slot />
  {/if}
</div>

<InfoDump url={$page.url.toString()} />

